import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAdminFilters } from "@/contexts/AdminFiltersContext";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Users, Search, Shield, Ban, CheckCircle, Loader2,
  Key, Clock, CreditCard, ChevronRight, X, Settings,
  LogOut, ShieldOff, AtSign, Mail, Forward, Activity,
  UserPlus, Copy, Check, Eye, RotateCcw, Zap, Timer,
  History,
} from "lucide-react";
import AdminEntitlementModal from "@/components/AdminEntitlementModal";
import AdminTimelineDialog from "@/components/AdminTimelineDialog";


/* ── Inline Sub-Components for User Detail ── */

function SecurityLimitRow({ settingKey, globalVal, overrideVal, effectiveVal, userId, onRefresh, isAdminUser = false }) {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState(overrideVal ?? "");
  const [saving, setSaving] = useState(false);
  const hasOverride = overrideVal !== null && overrideVal !== undefined;
  const label = settingKey.replace(/_/g, " ");

  // Keys enforced as hard numeric limits — bypassed for admins
  const LIMIT_KEYS = new Set([
    "max_free_aliases",
    "max_send_per_alias",
    "max_credit_cap",
    "initial_free_credits",
    "alias_ttl_minutes",
    "alias_reactivation_window_hours",
  ]);
  // Score thresholds — tracked but not enforced for admins (monitoring only)
  const SCORE_THRESHOLD_KEYS = new Set([
    "suspicious_score_threshold",
    "high_risk_score_threshold",
  ]);
  const isBypassedLimit = isAdminUser && LIMIT_KEYS.has(settingKey);
  const isBypassedScore = isAdminUser && SCORE_THRESHOLD_KEYS.has(settingKey);
  const isBypassed = isBypassedLimit || isBypassedScore;
  const bypassLabel = isBypassedScore ? "Monitoring only (Admin)" : "Unlimited (Admin)";
  const bypassTooltip = isBypassedScore
    ? "Scores are tracked but do not trigger blocking for admin users."
    : "Admin bypass — role-based";

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/security/users/${userId}/overrides/${settingKey}`, { value: Number(inputVal) || inputVal });
      toast.success(`Override set: ${settingKey}`);
      setEditing(false);
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await api.delete(`/admin/security/users/${userId}/overrides/${settingKey}`);
      toast.success(`Override removed: ${settingKey}`);
      onRefresh();
    } catch (e) { toast.error("Failed to remove override"); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 text-xs" data-testid={`limit-row-${settingKey}`}>
      <div className="flex-1 min-w-0">
        <span className="text-[#555555] capitalize">{label}</span>
      </div>
      <div className="flex items-center gap-1.5 tabular-nums shrink-0">
        {isBypassed ? (
          <span className="text-emerald-600 font-medium font-mono" title={bypassTooltip} data-testid={`limit-unlimited-${settingKey}`}>
            {bypassLabel}
          </span>
        ) : (
          <>
            <span className="text-[#AAAAAA]" title="Global">{globalVal}</span>
            {hasOverride && <span className="text-blue-600 font-medium" title="Override">→ {overrideVal}</span>}
            <span className={`font-medium ${hasOverride ? "text-blue-600" : "text-[#111111]"}`} title="Effective">= {effectiveVal}</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isBypassed ? (
          <span className="text-[10px] text-[#AAAAAA] italic pr-1">role-based</span>
        ) : editing ? (
          <>
            <Input type="number" value={inputVal} onChange={e => setInputVal(e.target.value)} className="w-16 h-6 text-xs text-center px-1" />
            <Button size="sm" className="h-6 px-2 text-[10px] bg-blue-600 text-white" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 size={10} className="animate-spin" /> : "Set"}
            </Button>
            <Button size="sm" variant="ghost" className="h-6 px-1 text-[10px]" onClick={() => setEditing(false)}>
              <X size={10} />
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-[#888888]" onClick={() => { setInputVal(overrideVal ?? globalVal); setEditing(true); }} data-testid={`limit-edit-${settingKey}`}>
              Edit
            </Button>
            {hasOverride && (
              <Button size="sm" variant="ghost" className="h-6 px-1 text-[10px] text-red-500" onClick={handleRemove} disabled={saving} title="Remove override">
                <RotateCcw size={10} />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function AliasRow({ alias, onRefresh, onTimeline }) {
  const [actionLoading, setActionLoading] = useState(null);
  const isPrimary = alias.is_primary;
  const lifecycle = alias.lifecycle_state || {};
  const status = lifecycle.status || (alias.expires_at && new Date(alias.expires_at) < new Date() ? "expired_recoverable" : "active");
  const reactivationPossible = lifecycle.reactivation_possible === true;
  const blockReason = lifecycle.block_reason;
  const recoverableUntil = lifecycle.recoverable_until;

  const handleExtend = async (minutes = 30) => {
    setActionLoading("extend");
    try {
      await api.post(`/admin/aliases/${alias.alias_id}/extend`, { minutes });
      toast.success(`Extended by ${minutes} min`);
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Extend failed"); }
    finally { setActionLoading(null); }
  };

  const handleReactivate = async () => {
    setActionLoading("reactivate");
    try {
      await api.post(`/admin/aliases/${alias.alias_id}/reactivate`);
      toast.success("Alias reactivated (60 min)");
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reactivation blocked"); }
    finally { setActionLoading(null); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  const renderStatusBadge = () => {
    if (isPrimary) return <Badge variant="outline" className="text-[8px] px-1 py-0 text-blue-600 border-blue-200">Primary</Badge>;
    if (status === "active") return <Badge variant="outline" className="text-[8px] px-1 py-0 text-emerald-600 border-emerald-200" data-testid={`alias-status-${alias.alias_id}`}>Active</Badge>;
    if (status === "expired_recoverable") return <Badge variant="outline" className="text-[8px] px-1 py-0 text-amber-600 border-amber-200" data-testid={`alias-status-${alias.alias_id}`} title={recoverableUntil ? `Recoverable until ${fmt(recoverableUntil)}` : ""}>Recoverable</Badge>;
    if (status === "expired_final") return <Badge variant="outline" className="text-[8px] px-1 py-0 text-red-600 border-red-200" data-testid={`alias-status-${alias.alias_id}`} title="Recovery window expired">Expired (final)</Badge>;
    if (status === "reassigned") return <Badge variant="outline" className="text-[8px] px-1 py-0 text-purple-600 border-purple-200" data-testid={`alias-status-${alias.alias_id}`} title="Address already reassigned">Reassigned</Badge>;
    return null;
  };

  const disabledReason =
    status === "expired_final" ? "Recovery window expired" :
    status === "reassigned" ? "Address reassigned to another user" :
    null;

  const showReactivate = !isPrimary && (status === "expired_recoverable" || status === "expired_final" || status === "reassigned");

  return (
    <div className="px-3 py-2.5 text-xs" data-testid={`alias-row-${alias.alias_id}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono text-[#111111] truncate">{alias.alias_address}</span>
        {renderStatusBadge()}
      </div>
      <div className="flex items-center gap-3 text-[#888888] flex-wrap">
        {alias.credits !== null && alias.credits !== undefined && (
          <span className="flex items-center gap-0.5"><Zap size={9} /> {alias.credits} credits</span>
        )}
        <span className="flex items-center gap-0.5"><Mail size={9} /> {alias.total_sent_count || 0} sent</span>
        {alias.expires_at && !isPrimary && (
          <span className="flex items-center gap-0.5"><Timer size={9} /> {status === "active" ? fmt(alias.expires_at) : `expired ${fmt(alias.expires_at)}`}</span>
        )}
        {status === "expired_recoverable" && recoverableUntil && (
          <span className="text-amber-600" data-testid={`alias-recoverable-until-${alias.alias_id}`}>Recoverable until {fmt(recoverableUntil)}</span>
        )}
      </div>
      {!isPrimary && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => handleExtend(30)} disabled={!!actionLoading}>
            {actionLoading === "extend" ? <Loader2 size={10} className="animate-spin" /> : "+30 min"}
          </Button>
          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => handleExtend(60)} disabled={!!actionLoading}>+1h</Button>
          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => handleExtend(1440)} disabled={!!actionLoading}>+24h</Button>
          {onTimeline && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[10px] text-[#555555]"
              onClick={() => onTimeline(alias)}
              title="View chronological activity for this alias"
              data-testid={`alias-timeline-${alias.alias_id}`}
            >
              <History size={10} className="mr-1" /> Timeline
            </Button>
          )}
          {showReactivate && (
            <Button
              size="sm"
              variant="outline"
              className={`h-6 px-2 text-[10px] ${reactivationPossible ? "border-emerald-200 text-emerald-600" : "border-gray-200 text-gray-400 cursor-not-allowed"}`}
              onClick={handleReactivate}
              disabled={!!actionLoading || !reactivationPossible}
              title={disabledReason || "Reactivate for 60 min"}
              data-testid={`alias-reactivate-${alias.alias_id}`}
            >
              {actionLoading === "reactivate" ? <Loader2 size={10} className="animate-spin" /> : "Reactivate"}
            </Button>
          )}
          {!reactivationPossible && disabledReason && showReactivate && (
            <span className="text-[10px] text-[#888888]" data-testid={`alias-block-reason-${alias.alias_id}`}>{disabledReason}</span>
          )}
        </div>
      )}
    </div>
  );
}



export default function AdminUsersPage() {
  // Unified filter: "all" | "admin" | "free" | "pro" | "em" | "system"
  const [filterKey, setFilterKey] = useState("all");
  const [showEmUsers, setShowEmUsers] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [proAccounts, setProAccounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);

  // Detail + Block dialog
  const [detailAccount, setDetailAccount] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [blockLoading, setBlockLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [freeDetail, setFreeDetail] = useState(null);
  const [freeDetailLoading, setFreeDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ local_part: "", domain: "", password: "", name: "", role: "user", plan: "free", pro_username: "", lifetime: false, alias_limit_override: "", can_use_reserved_aliases: false, reason: "" });
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [systemDomain, setSystemDomain] = useState("mail.quickinbox.app");

  // Phase 5 — timeline dialog targets (null or {id, email}/{alias_id, address})
  const [userTimelineTarget, setUserTimelineTarget] = useState(null);
  const [aliasTimelineTarget, setAliasTimelineTarget] = useState(null);

  // True binary presence — set of online user_ids (polled from /admin/presence).
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());

  const { hideTestData } = useAdminFilters();
  void hideTestData; // no longer drives this page; local "Show EM test users" is the single source of truth
  const fetchData = useCallback(async () => {
    try {
      // Single unified fetch from /admin/users. EM visibility is controlled ONLY
      // by the local "Show EM test users" toggle for this page.
      const params = showEmUsers ? "" : "?exclude_em=1";
      const [usersRes, proRes] = await Promise.all([
        api.get(`/admin/users${params}`),
        api.get("/admin/pro-accounts"),
      ]);
      setAllUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setProAccounts(proRes.data?.accounts || []);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [showEmUsers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll true presence every 30s while this page is mounted.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/presence");
        if (!cancelled) setOnlineUserIds(new Set(data.online_user_ids || []));
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Fetch system domain for Create User form
  useEffect(() => {
    api.get("/admin/config").then(({ data }) => {
      if (data.mail_domain) setSystemDomain(data.mail_domain);
    }).catch(() => {});
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchResults(data.results);
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  const openProDetail = async (accountId) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/pro-accounts/${accountId}`);
      setDetailAccount(data);
    } catch {
      toast.error("Failed to load account details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBlock = async () => {
    if (!blockTarget || !blockReason.trim()) return;
    setBlockLoading(true);
    try {
      await api.post(`/admin/pro-accounts/${blockTarget}/block`, { reason: blockReason.trim() });
      toast.success("Account blocked");
      setBlockTarget(null);
      setBlockReason("");
      setDetailAccount(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBlockLoading(false);
    }
  };

  const handleUnblock = async (accountId) => {
    try {
      await api.post(`/admin/pro-accounts/${accountId}/unblock`, { reason: "Unblocked via admin panel" });
      toast.success("Account unblocked");
      setDetailAccount(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openFreeDetail = async (userId) => {
    setFreeDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/detail`);
      setFreeDetail(data);
    } catch {
      toast.error("Failed to load user details");
    } finally {
      setFreeDetailLoading(false);
    }
  };

  const handleForceLogout = async (userId) => {
    setActionLoading("logout");
    try {
      await api.post(`/admin/users/${userId}/force-logout`);
      toast.success("All sessions invalidated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPro2FA = async (accountId) => {
    setActionLoading("reset2fa");
    try {
      await api.post(`/admin/pro-accounts/${accountId}/reset-2fa`, { reason: "Admin reset via panel" });
      toast.success("2FA reset successfully");
      openProDetail(accountId);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetAdmin2FA = async (userId) => {
    setActionLoading("reset2fa");
    try {
      await api.post(`/admin/users/${userId}/reset-admin-2fa`, { reason: "Admin reset via panel" });
      toast.success("Admin 2FA reset");
      openFreeDetail(userId);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async (userId) => {
    setActionLoading("impersonate");
    try {
      const { data } = await api.post(`/admin/users/${userId}/impersonate`);
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/impersonate?token=${encodeURIComponent(data.token)}`;
      window.open(url, "_blank", "noopener");
      toast.success(`Opened session for ${data.user?.email || "user"} in new tab`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateUser = async () => {
    setCreating(true);
    try {
      const payload = { ...createForm };
      // Use domain from form or fall back to system domain
      if (!payload.domain) payload.domain = systemDomain;
      if (payload.alias_limit_override) payload.alias_limit_override = parseInt(payload.alias_limit_override) || null;
      else delete payload.alias_limit_override;
      if (payload.plan !== "free") payload.create_pro = true;
      const { data } = await api.post("/admin/users/create", payload);
      setCreateResult(data);
      toast.success(`User ${data.email} created`);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({ local_part: "", domain: "", password: "", name: "", role: "user", plan: "free", pro_username: "", lifetime: false, alias_limit_override: "", can_use_reserved_aliases: false, reason: "" });
    setCreateResult(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={20} className="animate-spin text-[#888888]" />
      </div>
    );
  }

  // Client-side classification (Admin priority over Free/Pro).
  const isUserAdmin = (u) => u?.role === "admin" || u?.role === "superadmin";
  const isUserPro = (u) => !isUserAdmin(u) && u?.plan === "pro";
  const isUserFree = (u) => !isUserAdmin(u) && u?.plan !== "pro";
  const isUserEM = (u) => u?.created_by === "em";
  const isUserSystem = (u) => (u?.created_by || "system") === "system";
  // True binary presence — driven by /admin/presence heartbeat set.
  const isOnline = (u) => {
    const id = u?.user_id || u?._id || u?.id;
    return !!(id && onlineUserIds.has(id));
  };

  const predicateFor = (key) => ({
    all: () => true,
    admin: isUserAdmin,
    free: isUserFree,
    pro: isUserPro,
    em: isUserEM,
    system: isUserSystem,
  }[key] || (() => true));

  const filteredUsers = searchResults === null ? allUsers.filter(predicateFor(filterKey)) : [];
  const countsFor = (key) => {
    const list = allUsers.filter(predicateFor(key));
    const online = list.filter(isOnline).length;
    return { online, offline: list.length - online, total: list.length };
  };
  const activeCounts = countsFor(filterKey);

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-5 max-w-[1100px]" data-testid="admin-users-page">
        {/* Header + Search */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold text-[#111111] tracking-tight">Users</h2>
            <p className="text-sm text-[#888888] mt-0.5" data-testid="users-total-line">
              {allUsers.length} total · {allUsers.filter(isOnline).length} online
            </p>
          </div>

          <div className="flex items-center gap-2" data-testid="admin-user-search">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9" onClick={() => { resetCreateForm(); setCreateOpen(true); }} data-testid="admin-create-user-btn">
              <UserPlus size={14} className="mr-1.5" /> Create User
            </Button>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#888888]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Search by username, email, ID..."
                className="pl-8 h-9 w-64 text-sm"
                data-testid="admin-search-input"
              />
              {searchResults !== null && (
                <button onClick={() => { setSearchQuery(""); setSearchResults(null); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#888888] hover:text-[#888888]">
                  <X size={14} />
                </button>
              )}
            </div>
            <Button size="sm" onClick={handleSearch} disabled={searching} className="h-9 text-xs" data-testid="admin-search-btn">
              {searching ? <Loader2 size={14} className="animate-spin" /> : "Search"}
            </Button>
          </div>
        </div>

        {/* Unified filter chips — replaces Free/PRO tabs + Origin chips */}
        {searchResults === null && (
          <div className="space-y-2" data-testid="user-unified-filters">
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                { key: "all", label: "All" },
                { key: "admin", label: "Admin" },
                { key: "free", label: "Free" },
                { key: "pro", label: "Pro" },
                { key: "em", label: "EM" },
                { key: "system", label: "System" },
              ].map(({ key, label }) => {
                const c = countsFor(key);
                const active = filterKey === key;
                return (
                  <button
                    key={key}
                    onClick={() => setFilterKey(key)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${active ? "bg-zinc-900 text-white" : "bg-[#F5F5F5] text-[#555555] hover:bg-[#EBEBEB] hover:text-[#111111]"}`}
                    data-testid={`user-filter-${key}`}
                  >
                    {label} <span className={`ml-1 font-mono text-[10px] ${active ? "opacity-80" : "opacity-60"}`}>{c.total}</span>
                  </button>
                );
              })}
              <label className="flex items-center gap-1.5 ml-2 text-[11px] text-[#555555] cursor-pointer select-none" data-testid="show-em-toggle">
                <input
                  type="checkbox"
                  className="h-3 w-3 rounded border-[#D5D5D5] text-zinc-900 focus:ring-zinc-900"
                  checked={showEmUsers}
                  onChange={(e) => setShowEmUsers(e.target.checked)}
                  data-testid="show-em-checkbox"
                />
                Show EM test users
              </label>
            </div>
            {/* Compact per-filter stats */}
            <div className="flex items-center gap-3 text-[11px] font-mono text-[#555555]" data-testid="filter-stats">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span data-testid="stat-online">online {activeCounts.online}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <span data-testid="stat-offline">offline {activeCounts.offline}</span>
              </span>
              <span className="text-[#111111]" data-testid="stat-total">total {activeCounts.total}</span>
            </div>
          </div>
        )}

        {searchResults !== null && (
          <p className="text-xs text-[#888888]">{searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{searchQuery}"</p>
        )}

        {/* Table */}
        <div className="border border-[#E5E5E5] rounded-lg overflow-x-auto overflow-y-hidden bg-white" data-testid="users-table" style={{ WebkitOverflowScrolling: "touch" }}>
          {searchResults !== null ? (
            /* Global search results — unified view across all user types */
            searchResults.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-[#888888]" data-testid="search-empty">No users found</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F5F5F5] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">User</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Type</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Status</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Created</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {searchResults.map((r, i) => {
                    const isProSystem = !!r.account_id && !r.email; // PRO accounts collection
                    const identifier = isProSystem ? r.username : (r.name || r.email);
                    const sub = isProSystem ? r.account_id : r.email;
                    const isBlocked = isProSystem ? !!r.blocked_at : r.is_active === false;
                    const role = r.role;
                    const userId = r.user_id || r._id;
                    const plan = r.plan || (isProSystem ? "pro" : "free");
                    const userType = r._type || "free";

                    return (
                      <tr
                        key={i}
                        className="hover:bg-[#F7F7F7] cursor-pointer"
                        onClick={() => isProSystem ? openProDetail(r.account_id) : (userId && openFreeDetail(userId))}
                        data-testid={`search-row-${i}`}
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-[#111111] text-sm">{identifier || "—"}</p>
                          <p className="text-[10px] font-mono text-[#888888]">{sub}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {/* Plan badge */}
                            <Badge variant="outline" className={`text-[10px] font-mono ${plan === "pro" || plan === "enterprise" || isProSystem ? "border-blue-200 text-blue-600 bg-blue-50" : "border-[#E5E5E5] text-[#888888]"}`}>
                              {plan === "pro" || plan === "enterprise" || isProSystem ? "PRO" : "Free"}
                            </Badge>
                            {/* Role badge */}
                            {role === "superadmin" && (
                              <Badge variant="outline" className="text-[10px] font-mono border-red-200 text-red-600 bg-red-50">Superadmin</Badge>
                            )}
                            {role === "admin" && (
                              <Badge variant="outline" className="text-[10px] font-mono border-amber-200 text-amber-600 bg-amber-50">Admin</Badge>
                            )}
                            {role === "owner" && (
                              <Badge variant="outline" className="text-[10px] font-mono border-cyan-200 text-cyan-600 bg-cyan-50">Owner</Badge>
                            )}
                            {/* Test user indicator */}
                            {(r.email?.includes("test") || r.username?.includes("test") || r.stripe_customer_id === "admin_created") && (
                              <Badge variant="outline" className="text-[10px] font-mono border-violet-200 text-violet-600 bg-violet-50">Test</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {isBlocked ? (
                            <Badge className="bg-red-50 text-red-600 border-red-200 text-[10px]">Blocked</Badge>
                          ) : (
                            <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px]">Active</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#888888]">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3"><ChevronRight size={14} className="text-[#555555]" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          ) : (
            <FreeTable data={filteredUsers} onDetail={openFreeDetail} isOnline={isOnline} />
          )}
        </div>

        {/* Pro Detail Dialog */}
        <Dialog open={!!detailAccount} onOpenChange={() => setDetailAccount(null)}>
          <DialogContent className="sm:max-w-[500px] p-0" data-testid="pro-detail-modal">
            {detailAccount && (
              <div className="max-h-[85vh] overflow-y-auto p-6">
                <DialogHeader>
                  <DialogTitle className="text-lg">{detailAccount.username}</DialogTitle>
                  <DialogDescription className="text-xs font-mono text-[#888888]">{detailAccount.account_id}</DialogDescription>
                </DialogHeader>

                <div className="mt-4 space-y-3">
                  <DetailRow icon={Shield} label="Status" value={
                    detailAccount.blocked_at
                      ? <Badge className="bg-red-50 text-red-600 border-red-200 text-[10px]">Blocked</Badge>
                      : detailAccount.is_active
                        ? <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px]">Active</Badge>
                        : <Badge className="bg-[#F5F5F5] text-[#888888] border-[#E5E5E5] text-[10px]">Inactive</Badge>
                  } />
                  <DetailRow icon={CreditCard} label="Plan" value={
                    <Badge variant="outline" className="text-[10px] font-mono capitalize">{detailAccount.plan || "free"}{detailAccount.lifetime ? " (lifetime)" : ""}</Badge>
                  } />
                  <DetailRow icon={UserPlus} label="Origin" value={(() => {
                    const cb = detailAccount.created_by || "self";
                    const styles = cb === "admin"
                      ? "border-blue-200 text-blue-600 bg-blue-50"
                      : cb === "stripe"
                        ? "border-emerald-200 text-emerald-600 bg-emerald-50"
                        : "border-[#E5E5E5] text-[#888888] bg-[#F5F5F5]";
                    const label = cb === "admin" ? "Admin" : cb === "stripe" ? "Stripe" : "Self";
                    return (
                      <Badge variant="outline" className={`text-[10px] font-mono ${styles}`} data-testid="pro-detail-origin-badge">{label}</Badge>
                    );
                  })()} />
                  <DetailRow icon={Clock} label="Created" value={fmtDate(detailAccount.created_at)} />
                  <DetailRow icon={Clock} label="Last Login" value={fmtDate(detailAccount.last_login_at)} />
                  <DetailRow icon={Shield} label="2FA" value={detailAccount.tfa_enabled ? "Enabled" : "Off"} />
                  <DetailRow icon={Key} label="Recovery Codes" value={`${detailAccount.recovery_codes_remaining ?? "?"}/10 remaining`} />
                  <DetailRow icon={Shield} label="Session Version" value={detailAccount.session_version} />
                  <DetailRow icon={Users} label="Login Attempts (24h)" value={detailAccount.recent_login_attempts_24h} />

                  {/* Entitlement/privilege info */}
                  {(detailAccount.stripe_customer_id || detailAccount.alias_limit_override || detailAccount.can_use_reserved_aliases) && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs space-y-1">
                      {detailAccount.stripe_customer_id && <p className="text-blue-600">Stripe: <span className="font-mono">{detailAccount.stripe_customer_id}</span></p>}
                      {detailAccount.current_period_end && <p className="text-blue-600">Period end: {fmtDate(detailAccount.current_period_end)}</p>}
                      {detailAccount.alias_limit_override && <p className="text-blue-600">Alias limit override: {detailAccount.alias_limit_override}</p>}
                      {detailAccount.can_use_reserved_aliases && <p className="text-blue-600">Reserved aliases: allowed</p>}
                    </div>
                  )}

                  {detailAccount.blocked_at && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs">
                      <p className="font-medium text-red-900">Blocked: {fmtDate(detailAccount.blocked_at)}</p>
                      <p className="text-red-600 mt-0.5">Reason: {detailAccount.blocked_reason || "—"}</p>
                    </div>
                  )}

                  {detailAccount.last_payment && (
                    <div className="mt-3 p-3 bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-[#555555] mb-1.5">
                        <CreditCard size={12} /> Last Payment
                      </div>
                      <p className="text-xs text-[#888888]">Plan: {detailAccount.last_payment.plan_id}</p>
                      <p className="text-xs text-[#888888]">Amount: ${detailAccount.last_payment.amount} {detailAccount.last_payment.currency?.toUpperCase()}</p>
                      <p className="text-xs text-[#888888]">Paid: {fmtDate(detailAccount.last_payment.paid_at)}</p>
                    </div>
                  )}
                </div>

                <div className="mt-5 space-y-2">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setDetailAccount(null); setTimeout(() => setEditTarget(detailAccount), 100); }} className="text-xs flex-1" data-testid="detail-edit-btn">
                      <Settings size={13} className="mr-1.5" /> Edit Entitlements
                    </Button>
                    {detailAccount.blocked_at ? (
                      <Button size="sm" onClick={() => handleUnblock(detailAccount.account_id)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs flex-1" data-testid="detail-unblock-btn">
                        <CheckCircle size={13} className="mr-1.5" /> Unblock
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => { setDetailAccount(null); setTimeout(() => setBlockTarget(detailAccount.account_id), 100); }} className="border-red-300 text-red-600 hover:bg-red-50 text-xs flex-1" data-testid="detail-block-btn">
                        <Ban size={13} className="mr-1.5" /> Block
                      </Button>
                    )}
                  </div>
                  {detailAccount.tfa_enabled && (
                    <Button size="sm" variant="outline" onClick={() => handleResetPro2FA(detailAccount.account_id)} disabled={actionLoading === "reset2fa"} className="w-full text-xs border-amber-300 text-amber-600 hover:bg-amber-50" data-testid="detail-reset-2fa-btn">
                      {actionLoading === "reset2fa" ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <ShieldOff size={13} className="mr-1.5" />}
                      Reset 2FA
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Block Confirmation Dialog */}
        <Dialog open={!!blockTarget} onOpenChange={(open) => { if (!open) { setBlockTarget(null); setBlockReason(""); } }}>
          <DialogContent className="sm:max-w-[400px]" data-testid="block-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">Block Account</DialogTitle>
              <DialogDescription className="text-xs text-[#888888]">
                This will immediately block login, sessions, and all sensitive actions. Provide a reason.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <Input
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Reason for blocking..."
                className="text-sm"
                autoFocus
                data-testid="block-reason-input"
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setBlockTarget(null); setBlockReason(""); }} className="flex-1 text-xs" data-testid="block-cancel-btn">
                  Cancel
                </Button>
                <Button onClick={handleBlock} disabled={blockLoading || !blockReason.trim()} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs" data-testid="block-confirm-btn">
                  {blockLoading ? <Loader2 size={14} className="animate-spin" /> : "Block"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Entitlement Editor Modal */}
        <AdminEntitlementModal
          open={!!editTarget}
          account={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); fetchData(); }}
        />

        {/* Free User Detail Dialog */}
        <Dialog open={!!freeDetail} onOpenChange={() => setFreeDetail(null)}>
          <DialogContent className="sm:max-w-[520px] p-0" data-testid="free-detail-modal">
            {freeDetail && (
              <div className="max-h-[85vh] overflow-y-auto p-6">
                <DialogHeader>
                  <DialogTitle className="text-lg">{freeDetail.name || freeDetail.email}</DialogTitle>
                  <DialogDescription className="text-xs font-mono text-[#888888]">{freeDetail.email}</DialogDescription>
                </DialogHeader>

                <div className="mt-4 space-y-3">
                  {/* Activity Snapshot */}
                  <div className="p-3 bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg space-y-1.5">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mb-2">Activity Snapshot</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#888888] flex items-center gap-1.5"><Clock size={11} className="text-[#888888]" />Last Login</span>
                      <span className="text-[#555555] font-medium">{fmtDate(freeDetail.last_login)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#888888] flex items-center gap-1.5"><Activity size={11} className="text-[#888888]" />Last Action</span>
                      <span className="text-[#555555] font-medium">{fmtDate(freeDetail.last_action)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#888888] flex items-center gap-1.5"><Clock size={11} className="text-[#888888]" />Last Seen</span>
                      <span className="text-[#555555] font-medium">{fmtDate(freeDetail.last_seen_at)}</span>
                    </div>
                  </div>

                  {/* Abuse Shield — Risk level + last signal */}
                  {freeDetail.abuse_shield && (
                    <div className="p-3 bg-white border border-[#E5E5E5] rounded-lg space-y-2" data-testid="user-abuse-shield">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888]">Abuse Shield</p>
                        {(() => {
                          const lvl = freeDetail.abuse_shield.risk_level || "low";
                          const cls = lvl === "high"
                            ? "bg-red-50 text-red-700 border-red-200"
                            : lvl === "medium"
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200";
                          return (
                            <Badge variant="outline" className={`text-[10px] px-2 py-0.5 font-mono ${cls}`} data-testid="user-risk-pill">
                              risk: {lvl}
                            </Badge>
                          );
                        })()}
                      </div>
                      {/* Cooldown — only when active, visually subordinate */}
                      {freeDetail.abuse_shield.cooldown && freeDetail.abuse_shield.cooldown.active && freeDetail.abuse_shield.cooldown.expires_at && (
                        <div className="flex items-center justify-between text-[11px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1" data-testid="user-cooldown-indicator">
                          <span className="font-mono">Cooldown active</span>
                          <span className="font-mono tabular-nums" data-testid="user-cooldown-until">
                            until {new Date(freeDetail.abuse_shield.cooldown.expires_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-[#888888]">Score</span>
                        <span className="text-[#555555] font-mono tabular-nums" data-testid="user-abuse-score">{freeDetail.abuse_shield.score ?? 0}</span>
                      </div>
                      {freeDetail.abuse_shield.last_signal_type && (
                        <div className="flex items-center justify-between text-[11px]" data-testid="user-last-abuse-signal">
                          <span className="text-[#888888]">Last signal</span>
                          <span className="text-[#555555] font-mono">{String(freeDetail.abuse_shield.last_signal_type).replace(/_/g, " ")}</span>
                        </div>
                      )}
                      {freeDetail.abuse_shield.last_event && (
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-[#888888]">Last event</span>
                          <span className="text-[#555555] font-mono truncate max-w-[200px]" title={freeDetail.abuse_shield.last_event.action}>
                            {String(freeDetail.abuse_shield.last_event.action || "").replace(/_/g, " ")}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Usage Metrics */}
                  <div className="grid grid-cols-3 gap-2">
                    <MiniStat icon={AtSign} label="Aliases" value={freeDetail.usage?.aliases ?? 0} />
                    <MiniStat icon={Mail} label="Received" value={freeDetail.usage?.emails_received ?? 0} />
                    <MiniStat icon={Mail} label="Sent" value={freeDetail.usage?.emails_sent ?? 0} />
                    <MiniStat icon={Forward} label="Forwarded" value={freeDetail.usage?.emails_forwarded ?? 0} />
                    <MiniStat icon={Ban} label="Blocked" value={freeDetail.usage?.emails_blocked ?? 0} />
                    <MiniStat icon={Shield} label="Rules" value={freeDetail.usage?.active_rules ?? 0} />
                  </div>

                  {/* Engagement */}
                  <div className="space-y-1.5">
                    <DetailRow icon={CreditCard} label="Plan" value={
                      <Badge variant="outline" className="text-[10px] font-mono capitalize">{freeDetail.plan || "free"}</Badge>
                    } />
                    <DetailRow icon={Shield} label="Role" value={
                      <Badge variant="outline" className={`text-[10px] font-mono capitalize ${freeDetail.role === "superadmin" ? "border-red-200 text-red-600" : freeDetail.role === "admin" ? "border-amber-200 text-amber-600" : ""}`}>
                        {freeDetail.role || "user"}
                      </Badge>
                    } />
                    <DetailRow icon={Shield} label="Admin 2FA" value={freeDetail.tfa_enabled ? "Enabled" : "Off"} />
                    <DetailRow icon={Clock} label="Joined" value={fmtDate(freeDetail.created_at)} />
                  </div>
                </div>

                {/* Admin Bypass banner */}
                {(freeDetail.role === "admin" || freeDetail.role === "superadmin") && (
                  <div className="mt-4 p-2.5 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2" data-testid="admin-bypass-banner">
                    <ShieldOff size={14} className="text-amber-600 shrink-0" />
                    <span className="text-[11px] text-amber-800">
                      <strong>Admin Bypass active</strong> — role-based: send/alias/credit limits and abuse cooldowns do not apply. Scores and audit logs continue to accumulate.
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-5 space-y-2">
                  <Button size="sm" className="w-full text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleImpersonate(freeDetail.user_id)} disabled={actionLoading === "impersonate"} data-testid="impersonate-user-btn">
                    {actionLoading === "impersonate" ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Eye size={13} className="mr-1.5" />}
                    Login as User
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full text-xs"
                    onClick={() => setUserTimelineTarget({ id: freeDetail.user_id, email: freeDetail.email })}
                    data-testid="user-timeline-btn"
                  >
                    <History size={13} className="mr-1.5" />
                    View Timeline
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleForceLogout(freeDetail.user_id)} disabled={actionLoading === "logout"} className="w-full text-xs" data-testid="free-force-logout-btn">
                    {actionLoading === "logout" ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <LogOut size={13} className="mr-1.5" />}
                    Force Logout (Invalidate Sessions)
                  </Button>
                  {freeDetail.tfa_enabled && freeDetail.role !== "superadmin" && (
                    <Button size="sm" variant="outline" onClick={() => handleResetAdmin2FA(freeDetail.user_id)} disabled={actionLoading === "reset2fa"} className="w-full text-xs border-amber-300 text-amber-600 hover:bg-amber-50" data-testid="free-reset-2fa-btn">
                      {actionLoading === "reset2fa" ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <ShieldOff size={13} className="mr-1.5" />}
                      Reset Admin 2FA
                    </Button>
                  )}
                </div>

                {/* ── Security Limits ── */}
                {freeDetail.effective_limits && Object.keys(freeDetail.effective_limits).length > 0 && (
                  <div className="space-y-2 mt-4" data-testid="user-security-limits">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888]">Security Limits</p>
                    <div className="border border-[#E5E5E5] rounded-lg divide-y divide-[#E5E5E5] bg-white">
                      {Object.entries(freeDetail.effective_limits).map(([key, info]) => (
                        <SecurityLimitRow
                          key={key}
                          settingKey={key}
                          globalVal={info.global}
                          overrideVal={info.override}
                          effectiveVal={info.effective}
                          userId={freeDetail.user_id}
                          onRefresh={() => openFreeDetail(freeDetail.user_id)}
                          isAdminUser={freeDetail.role === "admin" || freeDetail.role === "superadmin"}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Aliases ── */}
                {freeDetail.aliases && freeDetail.aliases.length > 0 && (
                  <div className="space-y-2 mt-4" data-testid="user-aliases-section">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888]">Aliases ({freeDetail.aliases.length})</p>
                    <div className="border border-[#E5E5E5] rounded-lg divide-y divide-[#E5E5E5] bg-white">
                      {freeDetail.aliases.map(a => (
                        <AliasRow
                          key={a.alias_id}
                          alias={a}
                          onRefresh={() => openFreeDetail(freeDetail.user_id)}
                          onTimeline={(alias) => setAliasTimelineTarget({ alias_id: alias.alias_id, address: alias.alias_address })}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create User Modal */}
        <Dialog open={createOpen} onOpenChange={(open) => { if (!open) { setCreateOpen(false); setCreateResult(null); } }}>
          <DialogContent className="sm:max-w-[520px]" data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle className="text-lg">Create User</DialogTitle>
              <DialogDescription className="text-xs text-[#888888]">
                Create a new user account. PRO plans include a linked PRO account with recovery codes.
              </DialogDescription>
            </DialogHeader>

            {createResult ? (
              <CreateUserResult result={createResult} onDone={() => { setCreateOpen(false); setCreateResult(null); }} />
            ) : (
              <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
                {/* Local Part + Domain */}
                <div className="space-y-1.5" data-testid="create-address-section">
                  <Label className="text-xs">Email Address *</Label>
                  <div className="flex items-center gap-0">
                    <Input
                      value={createForm.local_part}
                      onChange={(e) => setCreateForm((f) => ({ ...f, local_part: e.target.value.replace(/[^a-zA-Z0-9._-]/g, "") }))}
                      placeholder="Support.Marc-01"
                      className="h-9 text-sm font-mono rounded-r-none border-r-0 flex-1"
                      data-testid="create-local-part"
                    />
                    <div className="h-9 px-2 flex items-center bg-[#F5F5F5] border-y border-[#E5E5E5] text-sm text-[#888888]">@</div>
                    <Input
                      value={createForm.domain || systemDomain}
                      onChange={(e) => setCreateForm((f) => ({ ...f, domain: e.target.value.toLowerCase() }))}
                      className="h-9 text-sm font-mono rounded-l-none border-l-0 flex-1"
                      data-testid="create-domain"
                    />
                  </div>
                  {createForm.local_part && (
                    <p className="text-[11px] font-mono text-[#888888]" data-testid="create-email-preview">
                      {createForm.local_part}@{createForm.domain || systemDomain}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Display Name</Label>
                    <Input value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} placeholder="Display name (optional)" className="h-9 text-sm" data-testid="create-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Password *</Label>
                    <Input type="password" value={createForm.password} onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} placeholder="Min. 8 characters" className="h-9 text-sm font-mono" data-testid="create-password" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Role</Label>
                    <Select value={createForm.role} onValueChange={(v) => setCreateForm((f) => ({ ...f, role: v }))}>
                      <SelectTrigger className="h-9 text-sm" data-testid="create-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="superadmin">Superadmin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Plan</Label>
                    <Select value={createForm.plan} onValueChange={(v) => setCreateForm((f) => ({ ...f, plan: v }))}>
                      <SelectTrigger className="h-9 text-sm" data-testid="create-plan"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="pro">PRO (Monthly)</SelectItem>
                        <SelectItem value="yearly">PRO (Yearly)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {createForm.plan !== "free" && (
                  <div className="space-y-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-blue-500">PRO Account Settings</p>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-blue-600">PRO Username</Label>
                      <Input value={createForm.pro_username} onChange={(e) => setCreateForm((f) => ({ ...f, pro_username: e.target.value.toLowerCase() }))} placeholder="pro login username" className="h-9 text-sm font-mono bg-white" data-testid="create-pro-username" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-blue-600">Lifetime access</Label>
                      <Switch checked={createForm.lifetime} onCheckedChange={(v) => setCreateForm((f) => ({ ...f, lifetime: v }))} data-testid="create-lifetime" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-blue-600">Alias limit override</Label>
                      <Input type="number" value={createForm.alias_limit_override} onChange={(e) => setCreateForm((f) => ({ ...f, alias_limit_override: e.target.value }))} placeholder="Leave empty for default" className="h-9 text-sm bg-white" data-testid="create-alias-limit" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-blue-600">Reserved aliases</Label>
                      <Switch checked={createForm.can_use_reserved_aliases} onCheckedChange={(v) => setCreateForm((f) => ({ ...f, can_use_reserved_aliases: v }))} data-testid="create-reserved" />
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-xs">Reason *</Label>
                  <Input value={createForm.reason} onChange={(e) => setCreateForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Why is this account being created?" className="h-9 text-sm" data-testid="create-reason" />
                </div>

                <DialogFooter>
                  <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)} className="text-xs">Cancel</Button>
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                    onClick={handleCreateUser}
                    disabled={creating || !createForm.local_part || !createForm.password || !createForm.reason}
                    data-testid="create-user-submit"
                  >
                    {creating ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <UserPlus size={13} className="mr-1.5" />}
                    Create User
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Phase 5 — Timeline dialogs */}
        <AdminTimelineDialog
          open={!!userTimelineTarget}
          onOpenChange={(v) => { if (!v) setUserTimelineTarget(null); }}
          scope="user"
          scopeId={userTimelineTarget?.id}
          title={userTimelineTarget?.email || ""}
          subtitle={userTimelineTarget?.id ? `ID: ${userTimelineTarget.id.slice(0, 12)}` : ""}
        />
        <AdminTimelineDialog
          open={!!aliasTimelineTarget}
          onOpenChange={(v) => { if (!v) setAliasTimelineTarget(null); }}
          scope="alias"
          scopeId={aliasTimelineTarget?.alias_id}
          title={aliasTimelineTarget?.address || ""}
          subtitle={aliasTimelineTarget?.alias_id ? `ID: ${aliasTimelineTarget.alias_id.slice(0, 12)}` : ""}
        />
      </div>
    </ScrollArea>
  );
}

function CreateUserResult({ result, onDone }) {
  const [copiedCodes, setCopiedCodes] = useState(false);
  const codes = result?.pro_account?.recovery_codes || [];

  const copyRecoveryCodes = () => {
    const text = codes.join("\n");
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  return (
    <div className="space-y-4 py-2" data-testid="create-user-result">
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200">
        <CheckCircle size={16} className="text-emerald-600 shrink-0" />
        <p className="text-sm font-medium text-emerald-600">User created successfully</p>
      </div>

      <div className="space-y-1.5 text-xs">
        <div className="flex justify-between"><span className="text-[#888888]">Email</span><span className="font-mono text-[#555555]">{result.email}</span></div>
        <div className="flex justify-between"><span className="text-[#888888]">Role</span><span className="font-medium text-[#555555]">{result.role}</span></div>
        <div className="flex justify-between"><span className="text-[#888888]">Plan</span><span className="font-medium text-[#555555]">{result.plan}</span></div>
      </div>

      {result.pro_account && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-blue-500">PRO Account</p>
          <div className="flex justify-between text-xs">
            <span className="text-blue-600">Username</span>
            <span className="font-mono text-blue-600">{result.pro_account.username}</span>
          </div>
          {codes.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-blue-600 font-medium">Recovery Codes</span>
                <button onClick={copyRecoveryCodes} className="text-blue-600 hover:text-blue-600 transition-colors" data-testid="copy-recovery-codes">
                  {copiedCodes ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                </button>
              </div>
              <div className="bg-white rounded border border-blue-200 p-2 font-mono text-[11px] text-blue-600 space-y-0.5">
                {codes.map((c, i) => <div key={i}>{c}</div>)}
              </div>
              <p className="text-[10px] text-blue-500 mt-1.5">Save these codes. They cannot be shown again.</p>
            </div>
          )}
        </div>
      )}

      <Button size="sm" className="w-full bg-zinc-900 text-white text-xs" onClick={onDone} data-testid="create-done-btn">Done</Button>
    </div>
  );
}

function ProTable({ data, onDetail, onBlock, onUnblock }) {
  if (!data.length) return <div className="px-4 py-8 text-center text-sm text-[#888888]">No PRO accounts</div>;
  return (
    <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
    <table className="w-full text-sm min-w-[720px]">
      <thead className="bg-[#F5F5F5] border-b border-[#E5E5E5]">
        <tr>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Username</th>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Origin</th>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Status</th>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">2FA</th>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Created</th>
          <th className="w-20"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#E5E5E5]">
        {data.map((a, i) => {
          const isBlocked = !!a.blocked_at;
          const cb = a.created_by || "self";
          const originStyles = cb === "admin"
            ? "border-blue-200 text-blue-600 bg-blue-50"
            : cb === "stripe"
              ? "border-emerald-200 text-emerald-600 bg-emerald-50"
              : "border-[#E5E5E5] text-[#888888] bg-[#F5F5F5]";
          const originLabel = cb === "admin" ? "Admin" : cb === "stripe" ? "Stripe" : "Self";
          return (
            <tr key={i} className="hover:bg-[#F7F7F7] cursor-pointer" onClick={() => onDetail(a.account_id)} data-testid={`pro-row-${i}`}>
              <td className="px-4 py-3">
                <p className="font-medium text-[#111111] text-sm">{a.username}</p>
                <p className="text-[10px] font-mono text-[#888888]">{a.account_id}</p>
              </td>
              <td className="px-4 py-3">
                <Badge variant="outline" className={`text-[10px] font-mono ${originStyles}`} data-testid={`pro-origin-badge-${i}`}>{originLabel}</Badge>
              </td>
              <td className="px-4 py-3">
                {isBlocked ? (
                  <Badge className="bg-red-50 text-red-600 border-red-200 text-[10px]">Blocked</Badge>
                ) : a.is_active ? (
                  <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px]">Active</Badge>
                ) : (
                  <Badge className="bg-[#F5F5F5] text-[#888888] border-[#E5E5E5] text-[10px]">Inactive</Badge>
                )}
              </td>
              <td className="px-4 py-3 text-xs text-[#888888]">{a.tfa_enabled ? "On" : "Off"}</td>
              <td className="px-4 py-3 text-xs text-[#888888]">{fmtDate(a.created_at)}</td>
              <td className="px-4 py-3">
                <ChevronRight size={14} className="text-[#555555]" />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

function FreeTable({ data, onDetail, isOnline }) {
  if (!data.length) return <div className="px-4 py-8 text-center text-sm text-[#888888]">No users</div>;
  return (
    <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
    <table className="w-full text-sm min-w-[720px]">
      <thead className="bg-[#F5F5F5] border-b border-[#E5E5E5]">
        <tr>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">User</th>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Role</th>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Origin</th>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Status</th>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Last Seen</th>
          <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Joined</th>
          <th className="w-10"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#E5E5E5]">
        {data.map((u, i) => {
          const userId = u.user_id || u._id;
          const online = typeof isOnline === "function" ? isOnline(u) : false;
          return (
            <tr key={i} className="hover:bg-[#F7F7F7] cursor-pointer" onClick={() => userId && onDetail(userId)} data-testid={`free-row-${i}`}>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`shrink-0 w-2 h-2 rounded-full ${online ? "bg-emerald-500" : "bg-red-500"}`}
                    title={online ? "Online" : "Offline"}
                    aria-label={online ? "Online" : "Offline"}
                    data-testid={`user-presence-${i}`}
                    data-online={online ? "1" : "0"}
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-[#111111] text-sm truncate">{u.name || "—"}</p>
                    <p className="text-[10px] font-mono text-[#111111] truncate">{u.email}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <Badge variant="outline" className={`text-[10px] font-mono capitalize ${u.role === "superadmin" ? "border-red-200 text-red-600" : u.role === "admin" ? "border-amber-200 text-amber-600" : ""}`}>
                  {u.role || "user"}
                </Badge>
              </td>
              <td className="px-4 py-3">
                {(() => {
                  const cb = u.created_by || "system";
                  const styles = cb === "admin"
                    ? "border-blue-200 text-blue-600 bg-blue-50"
                    : cb === "em"
                      ? "border-purple-200 text-purple-600 bg-purple-50"
                      : "border-[#E5E5E5] text-[#888888] bg-[#F5F5F5]";
                  const label = cb === "admin" ? "Admin" : cb === "em" ? "EM" : "System";
                  return (
                    <Badge variant="outline" className={`text-[10px] font-mono ${styles}`} data-testid={`user-origin-badge-${i}`}>{label}</Badge>
                  );
                })()}
              </td>
              <td className="px-4 py-3">
                <Badge className={`text-[10px] ${u.is_active !== false ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"}`}>
                  {u.is_active !== false ? "Active" : "Disabled"}
                </Badge>
              </td>
              <td className="px-4 py-3 text-xs text-[#888888]">{fmtDate(u.last_seen_at)}</td>
              <td className="px-4 py-3 text-xs text-[#888888]">{fmtDate(u.created_at)}</td>
              <td className="px-4 py-3">
                <ChevronRight size={14} className="text-[#555555]" />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="p-2.5 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-center">
      <Icon size={13} className="text-[#888888] mx-auto mb-1" />
      <p className="text-lg font-semibold text-[#111111] tabular-nums">{value}</p>
      <p className="text-[9px] font-mono uppercase tracking-widest text-[#888888]">{label}</p>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-[#888888] flex items-center gap-1.5"><Icon size={12} className="text-[#888888]" />{label}</span>
      <span className="text-xs text-[#555555] font-medium">{typeof value === "object" ? value : String(value ?? "—")}</span>
    </div>
  );
}

function fmtDate(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  } catch { return String(val); }
}
